import React, { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Avatar,
  Box,
  Button,
  Chip,
  ClickAwayListener,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
} from "@mui/material";
import { makeStyles } from "../../tss";
import { useNavigate } from "react-router-dom";
import { INVOICEPREVIEW_ROUTE, NOTIFICATION_ROUTE } from "../../constants/RoutePaths";
import { authHeader } from "../../common/mainfunction";
import { GET_NOTIFICATION_URL, SAVE_NOTIFICATION_DETAILS } from "../../common/url";
import { getUserId } from "../../common/function";
import API from "../../constants/API";
import moment from "moment";
import Loader from "../../common/Loader";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((_theme) => ({
  notifItem: {
    borderLeft: "3px solid #EC7C37",
    backgroundColor: "#FFF9F5",
    borderRadius: "0 8px 8px 0",
    marginBottom: "6px",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#FFF5F0",
    },
  },
  footerBtn: {
    textTransform: "none",
    fontWeight: 600,
    fontSize: "13px",
    padding: "4px 10px",
    minWidth: "auto",
    color: "#EC7C37",
    "&:hover": {
      backgroundColor: "#FFF5F0",
    },
  },
}));

interface NotificationEntry {
  notificationDetailsId: number;
  userId?: number;
  firstName?: string;
  lastName?: string;
  payload?: string;
  invoiceId?: number;
  supplierName?: string;
  createdDate?: string;
  isCompleted?: number;
  profileLogo?: string;
  [key: string]: unknown;
}

interface NotifyPopupProps {
  openFlag?: boolean;
  openList: () => void;
  setFlag: (flag: boolean) => void;
}

export default function NotifyPopup(props: NotifyPopupProps) {
  const { openList, setFlag } = props;
  const [_completList, setCompleteList] = useState<NotificationEntry[]>([]);
  const [notcompleteList, setNotCompleteList] = useState<NotificationEntry[]>([]);
  const [count, setCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [markingAll, setMarkingAll] = useState<boolean>(false);
  const styleclasses = useStyles();
  const history = useNavigate();
  const { t } = useTranslation("common");

  const getNotificationDetails = async () => {
    setLoading(true);
    const config = {
      method: "GET",
      url: GET_NOTIFICATION_URL,
      headers: authHeader(),
      params: { userId: getUserId() },
    };
    try {
      const response = await API(config);
      const { status, messageCount, readedMessages, unreadedMessages } = response.data;
      if (status === "Success") {
        setCompleteList(readedMessages || []);
        setNotCompleteList(unreadedMessages || []);
        setCount(messageCount?.[0]?.totalCount || 0);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getNotificationDetails();
  }, []);

  const handleClick = (invoiceId: any) => {
    history(`${INVOICEPREVIEW_ROUTE}/${invoiceId}`, {
      state: { locationChange: true },
    });
    openList();
  };

  const markAllAsRead = async () => {
    if (!notcompleteList || notcompleteList.length === 0) return;
    setMarkingAll(true);
    try {
      const promises = notcompleteList.map((n) =>
        API({
          method: "POST",
          url: SAVE_NOTIFICATION_DETAILS,
          headers: authHeader(),
          data: {
            notificationDetailsId: n.notificationDetailsId,
            userId: n.userId?.toString(),
            tag: n.tag || "Comments",
            payload: n.payload,
            invoiceId: n.invoiceId,
            isRead: 1,
            commentId: n.commentId || 0,
          },
        })
      );
      await Promise.all(promises);
      setCompleteList((prev) => [...prev, ...notcompleteList.map((n) => ({ ...n, isRead: 1 }))]);
      setNotCompleteList([]);
      setCount(0);
    } catch (error) {
      console.error("Error marking all as read:", error);
    } finally {
      setMarkingAll(false);
    }
  };

  const viewMoreFun = () => {
    openList();
    history(NOTIFICATION_ROUTE);
  };

  const getStatusChip = (invoiceStatus: any) => {
    const statusMap = {
      Pending: { label: "Pending", color: "#EC7C37" },
      Approved: { label: "Approved", color: "#22C55E" },
      Rejected: { label: "Rejected", color: "#EF4444" },
      "Reject Invoice": { label: "Rejected", color: "#EF4444" },
      "Request Information": { label: "Request Info", color: "#F59E0B" },
      Initializing: { label: "Initializing", color: "#A0A3BD" },
    };
    const config = (statusMap as Record<string, { label: string; color: string }>)[
      invoiceStatus
    ] || {
      label: invoiceStatus || "Unknown",
      color: "#A0A3BD",
    };
    return (
      <Chip
        label={config.label}
        size="small"
        sx={{
          backgroundColor: config.color,
          color: "white",
          fontWeight: 600,
          fontSize: "11px",
          height: "20px",
          mt: 0.5,
          display: "inline-flex",
        }}
      />
    );
  };

  const displayList = notcompleteList.slice(0, 4);

  return (
    <ClickAwayListener onClickAway={() => setFlag(false)}>
      <div className="notpop-screen" style={{ left: "160px", top: "210px" }}>
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            fontWeight: 700,
            color: "#14142A",
            fontSize: "20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {t("nav.notifications")}
          {count > 0 && (
            <Chip
              label={count}
              size="small"
              sx={{
                backgroundColor: "#EC7C37",
                color: "white",
                fontWeight: 700,
                height: "22px",
                fontSize: "12px",
              }}
            />
          )}
          <IconButton aria-label="close" onClick={openList} sx={{ color: "#A0A3BD" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ padding: "0 16px 12px !important" }}>
          <Box sx={{ maxHeight: "55vh", overflowY: "auto" }}>
            {loading && (
              <Box sx={{ textAlign: "center", py: 3 }}>
                <Loader />
              </Box>
            )}

            {!loading && displayList.length === 0 && (
              <Typography variant="body2" sx={{ color: "#A0A3BD", textAlign: "center", py: 3 }}>
                No unread notifications
              </Typography>
            )}

            {!loading && displayList.length > 0 && (
              <List sx={{ p: 0 }}>
                {displayList.map((rec) => {
                  const {
                    profileLogo,
                    createdDate,
                    firstName,
                    lastName,
                    payload,
                    invoiceId,
                    invoiceStatus,
                    notificationDetailsId,
                  } = rec;
                  const displayName = `${firstName || ""} ${lastName || ""}`.trim() || "Unknown";
                  const initials =
                    `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase() || "?";
                  return (
                    <ListItem
                      key={notificationDetailsId}
                      className={styleclasses.notifItem}
                      onClick={() => handleClick(invoiceId)}
                      alignItems="flex-start"
                      sx={{ pr: 1 }}
                    >
                      <ListItemAvatar sx={{ minWidth: 44 }}>
                        <Avatar
                          src={profileLogo}
                          sx={{ width: 36, height: 36, bgcolor: "#EC7C37" }}
                        >
                          {initials}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        disableTypography
                        primary={
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              mb: 0.25,
                            }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 600,
                                fontSize: "13px",
                                color: "#14142A",
                              }}
                            >
                              {displayName}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "11px",
                                color: "#A0A3BD",
                                ml: 1,
                                flexShrink: 0,
                              }}
                            >
                              {moment(createdDate).fromNow()}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography
                              sx={{
                                fontSize: "12px",
                                color: "#6E7191",
                                whiteSpace: "normal",
                                wordBreak: "break-word",
                              }}
                            >
                              {payload}
                            </Typography>
                            {invoiceStatus && (
                              <Box sx={{ mt: 0.5 }}>{getStatusChip(invoiceStatus)}</Box>
                            )}
                          </>
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Box>

          <Divider sx={{ mt: 1, mb: 1 }} />

          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            {notcompleteList.length > 0 && (
              <Button
                className={styleclasses.footerBtn}
                onClick={markAllAsRead}
                disabled={markingAll}
              >
                {markingAll ? "Marking..." : "Mark all as read"}
              </Button>
            )}
            <Button className={styleclasses.footerBtn} onClick={viewMoreFun} sx={{ ml: "auto" }}>
              View More
            </Button>
          </Box>
        </DialogContent>
      </div>
    </ClickAwayListener>
  );
}
